/**
 * Navigation Configuration
 *
 * Defines navigation items for both User and IT portals.
 * The User portal has minimal navigation focused on self-service,
 * while the IT portal has full access to ticketing, KB management, and admin.
 */

import {
  ConfirmationNumber as TicketingIcon,
  WorkOutline as MyWorkIcon,
  ListAlt as AllTicketsIcon,
  Add as CreateTicketIcon,
  MenuBook as KnowledgeBaseIcon,
  Article as ArticlesIcon,
  AdminPanelSettings as AdminIcon,
  People as UsersIcon,
  MonitorHeart as TechStatusIcon,
  Analytics as StatsIcon,
  HelpOutline as HelpIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import type { NavigationConfig, NavItem } from '../../../types/navigation';
import type { PortalType } from '../../../contexts/PortalContext';

// ============================================
// User Portal Navigation
// ============================================

/**
 * Navigation items for the User (end-user) portal.
 * Focused on self-service: getting help and browsing knowledge base.
 */
export const userPortalNavigation: NavigationConfig = {
  systems: [
    {
      id: 'ticketing',
      label: 'Support',
      icon: <HelpIcon />,
      basePath: '/help',
      defaultExpanded: true,
      items: [
        {
          id: 'get-help',
          label: 'Get Help',
          icon: <HelpIcon />,
          path: '/help',
        },
        {
          id: 'my-requests',
          label: 'My Requests',
          icon: <TicketingIcon />,
          path: '/my-requests',
        },
      ],
    },
    {
      id: 'knowledge-base',
      label: 'Knowledge Base',
      icon: <KnowledgeBaseIcon />,
      basePath: '/kb',
      items: [
        {
          id: 'browse-articles',
          label: 'Browse Articles',
          icon: <ArticlesIcon />,
          path: '/kb',
        },
        {
          id: 'search-kb',
          label: 'Search',
          icon: <SearchIcon />,
          path: '/kb?search=true',
        },
      ],
    },
  ],
};

// ============================================
// IT Portal Navigation
// ============================================

/**
 * Navigation items for the IT (technician/admin) portal.
 * Full access to ticketing, KB management, and admin functions.
 */
export const itPortalNavigation: NavigationConfig = {
  systems: [
    {
      id: 'ticketing',
      label: 'Ticketing',
      icon: <TicketingIcon />,
      basePath: '/ticketing',
      defaultExpanded: true,
      items: [
        {
          id: 'create-ticket',
          label: 'Create Ticket',
          icon: <CreateTicketIcon />,
          path: '/tickets/new',
        },
        {
          id: 'my-work',
          label: 'My Work',
          icon: <MyWorkIcon />,
          path: '/ticketing/my-work',
        },
        {
          id: 'all-tickets',
          label: 'All Tickets',
          icon: <AllTicketsIcon />,
          path: '/ticketing/all-tickets',
        },
      ],
    },
    {
      id: 'knowledge-base',
      label: 'Knowledge Base',
      icon: <KnowledgeBaseIcon />,
      basePath: '/knowledge-base',
      items: [
        {
          id: 'articles',
          label: 'Articles',
          icon: <ArticlesIcon />,
          path: '/knowledge-base/articles',
        },
      ],
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: <AdminIcon />,
      basePath: '/admin',
      items: [
        {
          id: 'users',
          label: 'User Management',
          icon: <UsersIcon />,
          path: '/admin/users',
        },
        {
          id: 'tech-status',
          label: 'Tech Status',
          icon: <TechStatusIcon />,
          path: '/admin/tech-status',
        },
        {
          id: 'stats',
          label: 'Stats',
          icon: <StatsIcon />,
          path: '/admin/stats',
        },
      ],
    },
  ],
};

// ============================================
// Navigation Getters
// ============================================

/**
 * Get navigation configuration for a specific portal
 */
export function getNavigationForPortal(portal: PortalType): NavigationConfig {
  return portal === 'it' ? itPortalNavigation : userPortalNavigation;
}

/**
 * Get flat list of all navigation items for a portal
 */
export function getAllNavItems(portal: PortalType): NavItem[] {
  const config = getNavigationForPortal(portal);
  return config.systems.flatMap((system) => system.items);
}

/**
 * Find a navigation item by path
 */
export function findNavItemByPath(portal: PortalType, path: string): NavItem | null {
  const items = getAllNavItems(portal);
  return items.find((item) => item.path === path) ?? null;
}

// ============================================
// Legacy Export (for backward compatibility)
// ============================================

/**
 * @deprecated Use getNavigationForPortal() instead
 * This export is kept for backward compatibility during migration.
 */
export const navigationConfig = itPortalNavigation;
